import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CaptchaWidgetRef } from "@/UI/CaptchaWidget/CaptchaWidget";
import { LS_USER_TOKEN } from "@/Constants/LSConstants";
import {
  useAuthorizationMutation,
  useRegistrationMutation,
} from "../../Store/APIs/Auth.api";
import { throwNewToastError } from "../../Utils/MainUtils";
import AuthWindow from "./Components/AuthWindow";
import {
  ERROR_INCORRECT_EMAIL_OR_PASSWORD,
  ERROR_USER_ALREADY_EXIST,
} from "./Constants/AuthErrorsList";
import { validationAuthForm } from "./Helpers/ValidationHelpers";
import styles from "./page.module.scss";

export interface IUserData {
  username: string;
  password: string;
  retryPassword?: string;
  authMode: "singIn" | "singUp";
}

interface IAuthPageProps {
  // captchaToken: string;
  authMode: "singIn" | "singUp";
}

const AuthPage: React.FC<IAuthPageProps> = ({ authMode }) => {
  const navigate = useNavigate();
  const { refKey, authToken } = useParams();

  useEffect(() => {
    if (authToken) {
      localStorage.clear();
      localStorage.setItem(LS_USER_TOKEN, authToken);
      navigate("/", { replace: true });
    }
  }, [authToken]);

  const [authorization] = useAuthorizationMutation();
  const [registration] = useRegistrationMutation();

  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<CaptchaWidgetRef | null>(null);

  const handleSetToken = (token: string) => {
    setCaptchaToken(token);
  };

  const handleClearErrorsList = () => {
    setErrorsList([]);
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken("");
  };

  const handleSubmit = ({
    username,
    password,
    retryPassword,
    authMode,
  }: IUserData) => {
    const errorsList = validationAuthForm(
      username,
      password,
      authMode,
      captchaToken,
      retryPassword
    );

    if (errorsList.length) {
      setErrorsList(errorsList);
      return;
    } else {
      try {
        if (authMode === "singIn") {
          authorization({
            username: username.trim(),
            password: password.trim(),
            captchaToken,
          })
            .unwrap()
            .then((result) => {
              if (result && result.token) {
                navigate("/");
              }
            })
            .catch((error) => {
              if (error && "status" in error && error.status === 401) {
                setErrorsList((prevState) => [
                  ...prevState,
                  ERROR_INCORRECT_EMAIL_OR_PASSWORD,
                ]);
                resetCaptcha();
              }
              if (error && "status" in error && error.status === 403) {
                throwNewToastError("An error occurred, please try later");
                resetCaptcha();
              }
              if (error && "data" in error && error.data) {
                throwNewToastError(error.data);
                resetCaptcha();
              }
            });
        }
        if (authMode === "singUp") {
          registration({
            username: username.trim(),
            password: password.trim(),
            captchaToken,
            referral: refKey,
          })
            .unwrap()
            .then((result) => {
              if (result && result.token) {
                navigate("/");
              }
            })
            .catch((error) => {
              if (error && "data" in error && error.status === 500) {
                setErrorsList((prevState) => [
                  ...prevState,
                  ERROR_USER_ALREADY_EXIST,
                ]);
              } else if (error && "status" in error && error.status === 403) {
                throwNewToastError("An error occurred, please try later");
              } else {
                console.error(error);
              }
            });
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div
      className={"g-recaptcha" + " " + styles.page}
      data-sitekey="_reCAPTCHA_site_key_"
      data-size="invisible"
    >
      <div className={styles.logo_block}>
        <div className={styles.logo}></div>
        <div className={styles.stores}>
          <div className={styles.store}>
            <div className={`${styles.store_icon} ${styles.google}`} />
            <span className={styles.store_text}>
              Installations for your apps <br />
              on Google Play and App Store
            </span>
            <div className={`${styles.store_icon} ${styles.appstore}`} />
          </div>
        </div>
      </div>
      <div className={styles.form_block}>
        <AuthWindow
          handleSubmit={handleSubmit}
          validationErrors={errorsList}
          handleClearErrorsList={handleClearErrorsList}
          authMode={authMode}
          refKey={refKey}
          setToken={handleSetToken}
          captchaRef={captchaRef}
        />
      </div>
    </div>
  );
};

export default AuthPage;
